import { captureScreenshot, CAPTURE_EXCLUDE_ATTR } from "./screenshot.js";

// REUSABLE Figma-style comment flow: the cursor becomes a comment pin, the user
// clicks a spot on the app, a pin drops there and a one-line compose bar opens
// IN PLACE. Captures a clean screenshot of the view with the pin burned in at
// the clicked location. Resolves { text, dataUrl } or null if cancelled.
const Z = 2147483000;
const PIN = "#ef4444";

// Comment-bubble cursor (hotspot at the bottom-left tip, like Figma's pin).
const COMMENT_CURSOR =
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 24 24'%3E%3Cpath fill='%23ef4444' stroke='white' stroke-width='1.5' d='M4 4h16v11H9l-5 5z'/%3E%3C/svg%3E\") 4 22, crosshair";

export function leaveComment() {
    return new Promise((resolve) => {
        const overlay = document.createElement("div");
        overlay.setAttribute(CAPTURE_EXCLUDE_ATTR, "");
        overlay.style.cssText = `position:fixed;inset:0;z-index:${Z};background:rgba(59,130,246,.04);cursor:${COMMENT_CURSOR};`;

        const hint = document.createElement("div");
        hint.setAttribute(CAPTURE_EXCLUDE_ATTR, "");
        hint.textContent = "Click where you want to comment  ·  Esc to cancel";
        hint.style.cssText = `position:fixed;top:16px;left:50%;transform:translateX(-50%);z-index:${
            Z + 2
        };background:#111827;color:#fff;font:600 13px system-ui;padding:8px 16px;border-radius:999px;box-shadow:0 6px 20px rgba(0,0,0,.3);pointer-events:none;`;

        const onKey = (e) => {
            if (e.key === "Escape") end(null);
        };
        const cleanupNodes = [];
        let ended = false;
        const end = (result) => {
            if (ended) return;
            ended = true;
            window.removeEventListener("keydown", onKey);
            overlay.remove();
            hint.remove();
            cleanupNodes.forEach((n) => n.remove());
            resolve(result);
        };
        window.addEventListener("keydown", onKey);

        let capturing = false;
        overlay.onclick = async (e) => {
            if (capturing) return; // ignore re-entrant clicks during capture
            capturing = true;
            const xPct = e.clientX / window.innerWidth;
            const yPct = e.clientY / window.innerHeight;
            const px = e.clientX;
            const py = e.clientY;
            // KEEP the overlay as a full-screen pointer shield during the async
            // capture — it's excluded from the screenshot (CAPTURE_EXCLUDE_ATTR),
            // so it can't taint the image, but it blocks stray clicks from
            // hitting real buttons and mutating the DOM mid-capture. Only the
            // hint is dropped. The shield is removed once the compose bar opens.
            hint.remove();
            overlay.style.cursor = "progress";
            const shot = await captureScreenshot();
            if (ended) return; // cancelled (Esc) while capturing
            const dataUrl = shot ? await burnPin(shot, xPct, yPct) : null;
            if (ended) return;
            overlay.remove();
            openCompose(px, py, dataUrl, end, cleanupNodes);
        };

        document.body.appendChild(overlay);
        document.body.appendChild(hint);
    });
}

// Inline one-line compose bar anchored at the clicked point (Figma-style).
function openCompose(px, py, dataUrl, end, cleanupNodes) {
    // Backdrop: an outside click cancels; the pin/bar sit above it.
    const backdrop = document.createElement("div");
    backdrop.setAttribute(CAPTURE_EXCLUDE_ATTR, "");
    backdrop.style.cssText = `position:fixed;inset:0;z-index:${Z};`;
    backdrop.onclick = () => end(null);
    cleanupNodes.push(backdrop);

    const pin = document.createElement("div");
    pin.setAttribute(CAPTURE_EXCLUDE_ATTR, "");
    pin.style.cssText = `position:fixed;left:${px - 12}px;top:${
        py - 24
    }px;z-index:${
        Z + 1
    };width:24px;height:24px;border-radius:50% 50% 50% 0;background:${PIN};border:2.5px solid #fff;box-shadow:0 4px 12px rgba(239,68,68,.5);transform-origin:bottom left;animation:agentui-pin-drop .22s cubic-bezier(.34,1.56,.64,1) both;`;
    cleanupNodes.push(pin);

    const bar = document.createElement("div");
    bar.setAttribute(CAPTURE_EXCLUDE_ATTR, "");
    cleanupNodes.push(bar); // so end() actually removes it
    // Clamp within the viewport (bar is ~320px wide, ~52px tall).
    const left = Math.min(Math.max(px + 8, 8), window.innerWidth - 328);
    const top = Math.min(py + 6, window.innerHeight - 64);
    bar.style.cssText = `position:fixed;left:${left}px;top:${top}px;z-index:${
        Z + 1
    };display:flex;align-items:center;gap:8px;background:#fff;border-radius:16px;padding:8px 8px 8px 14px;box-shadow:0 12px 40px rgba(0,0,0,.22),0 0 0 1px rgba(0,0,0,.04);width:320px;box-sizing:border-box;animation:agentui-pop .16s ease-out;`;
    bar.onclick = (e) => e.stopPropagation();

    // Leading comment-bubble glyph.
    const lead = document.createElement("span");
    lead.setAttribute(CAPTURE_EXCLUDE_ATTR, "");
    lead.style.cssText =
        "flex:0 0 auto;display:inline-flex;color:#ef4444;";
    lead.innerHTML =
        "<svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z'/></svg>";

    const input = document.createElement("input");
    input.type = "text";
    input.maxLength = 2000;
    input.placeholder = "Add a comment";
    input.style.cssText =
        "flex:1;border:none;outline:none;font:14px/1.2 system-ui;background:transparent;color:#111827;min-width:0;";

    const send = document.createElement("button");
    send.setAttribute("aria-label", "Send comment");
    send.style.cssText =
        "flex:0 0 auto;border:none;width:34px;height:34px;border-radius:50%;background:linear-gradient(135deg,#3b82f6,#2563eb);color:#fff;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;box-shadow:0 3px 10px rgba(37,99,235,.45);transition:transform .1s;";
    send.innerHTML =
        "<svg width='17' height='17' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='m22 2-7 20-4-9-9-4Z'/><path d='M22 2 11 13'/></svg>";
    send.onmousedown = () => (send.style.transform = "scale(.9)");
    send.onmouseup = () => (send.style.transform = "scale(1)");
    const submit = () => {
        const text = input.value.trim();
        if (!text) {
            input.focus();
            return;
        }
        end({ text, dataUrl });
    };
    send.onclick = submit;
    input.onkeydown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            submit();
        }
        if (e.key === "Escape") end(null);
    };

    // Pop-in keyframes (scoped, injected once).
    if (!document.getElementById("agentui-pin-style")) {
        const style = document.createElement("style");
        style.id = "agentui-pin-style";
        style.textContent =
            "@keyframes agentui-pop{from{opacity:0;transform:scale(.94) translateY(4px)}to{opacity:1;transform:none}}@keyframes agentui-pin-drop{from{transform:rotate(-45deg) scale(0)}to{transform:rotate(-45deg) scale(1)}}";
        document.head.appendChild(style);
    }

    bar.appendChild(lead);
    bar.appendChild(input);
    bar.appendChild(send);
    document.body.appendChild(backdrop);
    document.body.appendChild(pin);
    document.body.appendChild(bar);
    input.focus();
}

// Draw a teardrop comment pin onto the screenshot at the normalized location.
function burnPin(dataUrl, xPct, yPct) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);

            const x = xPct * canvas.width;
            const y = yPct * canvas.height;
            const r = Math.max(14, canvas.width / 60);
            ctx.fillStyle = PIN;
            ctx.strokeStyle = "#fff";
            ctx.lineWidth = Math.max(2, r / 6);
            ctx.beginPath();
            ctx.arc(x, y - r, r, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(x - r * 0.5, y - r * 0.6);
            ctx.lineTo(x, y + r * 0.5);
            ctx.lineTo(x + r * 0.5, y - r * 0.6);
            ctx.closePath();
            ctx.fill();
            ctx.fillStyle = "#fff";
            ctx.beginPath();
            ctx.arc(x, y - r, r * 0.32, 0, Math.PI * 2);
            ctx.fill();

            resolve(canvas.toDataURL("image/jpeg", 0.85));
        };
        img.onerror = () => resolve(null);
        img.src = dataUrl;
    });
}
